// enhanced-xhr.ts
type XHROpenArgs = [method: string, url: string, async?: boolean, username?: string, password?: string];

interface EnhancedXMLHttpRequest extends XMLHttpRequest {
    _openArgs?: XHROpenArgs;
}

class EnhancedXHR {
    private static errorMap: Record<string, number> = {};

    private static isDebug = false;

    // 每个 URL 的阈值
    private static maxErrorCount = 3;

    // 黑名单 URL 列表
    private static blackList: string[] = [];

    // 回调函数，当某个 URL 超过错误阈值时调用
    private static limitCallback?: (url: string, count: number) => void;

    private static OriginalOpen = XMLHttpRequest.prototype.open;

    private static OriginalSend = XMLHttpRequest.prototype.send;

    static enable() {
        // eslint-disable-next-line func-names
        XMLHttpRequest.prototype.open = function (...args: any) {
            const shouldIntercept = EnhancedXHR.blackList.some((bad) => args[1].includes(bad));
            if (shouldIntercept && EnhancedXHR.isDebug) {
                console.log('[EnhancedXHR] Intercepted open:', args);
            }
            const xhr = this as EnhancedXMLHttpRequest;
            xhr._openArgs = args;
            // eslint-disable-next-line no-eval
            return EnhancedXHR.OriginalOpen.apply(this, args);
        };

        // eslint-disable-next-line func-names
        XMLHttpRequest.prototype.send = function (body?: Document | BodyInit | null) {
            const xhr = this as EnhancedXMLHttpRequest;
            const url = xhr._openArgs?.[1] ?? '';

            // 只拦截黑名单里的 URL
            const shouldIntercept = EnhancedXHR.blackList.some((bad) => url.includes(bad));
            if (!shouldIntercept) {
                return EnhancedXHR.OriginalSend.call(this, body);
            }

            // 已超过错误阈值，直接拦截
            if (EnhancedXHR.errorMap[url] >= EnhancedXHR.maxErrorCount) {
                if (EnhancedXHR.isDebug) {
                    console.warn(`[EnhancedXHR] Blocked request to "${url}" due to too many errors`);
                }
                if (EnhancedXHR.limitCallback) {
                    EnhancedXHR.limitCallback(url, EnhancedXHR.errorMap[url]);
                }
                // eslint-disable-next-line consistent-return
                return;
            }

            // 监听请求完成
            xhr.addEventListener('loadend', () => {
                if (xhr.status !== 200) {
                    EnhancedXHR.errorMap[url] = typeof EnhancedXHR.errorMap[url] !== 'number' ? 1 : EnhancedXHR.errorMap[url] + 1;
                    if (EnhancedXHR.isDebug) {
                        console.error(`[EnhancedXHR] Request failed (${xhr.status}) - ${url} | errors: ${EnhancedXHR.errorMap[url]}`);
                    }
                }
            });

            return EnhancedXHR.OriginalSend.call(this, body);
        };
    }

    static resetErrorCount(url?: string) {
        if (url) {
            EnhancedXHR.errorMap[url] = 0;
        } else {
            EnhancedXHR.errorMap = {};
        }
    }

    static setMaxErrorCount(limit: number) {
        EnhancedXHR.maxErrorCount = limit;
    }

    static setBlackList(urls: string[], callback?: (url: string, count: number) => void) {
        EnhancedXHR.blackList = urls;
        EnhancedXHR.limitCallback = callback;
    }

    static setIsDebug(isDebug: boolean) {
        EnhancedXHR.isDebug = isDebug;
    }
}

export default EnhancedXHR;
